import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { blendApi, Target, FamilyRow } from "./api";
import { GENDER_AR } from "./ui";

export default function ExplorePage() {
  const nav = useNavigate();
  const [targets, setTargets] = useState<Target[]>([]);
  const [families, setFamilies] = useState<FamilyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [house, setHouse] = useState("");
  const [gender, setGender] = useState("");
  const [family, setFamily] = useState("");

  useEffect(() => {
    Promise.all([blendApi.targets({}), blendApi.families()])
      .then(([t, f]) => { setTargets(t); setFamilies(f); })
      .catch((e) => setErr(String(e.message || e)))
      .finally(() => setLoading(false));
  }, []);

  const houses = useMemo(() => Array.from(new Set(targets.map((t) => t.house))).sort(), [targets]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return targets.filter((t) =>
      (!house || t.house === house) &&
      (!gender || t.gender === gender) &&
      (!family || t.family === family) &&
      (!q || [t.name, t.house, t.dna, t.key_chem].join(" ").toLowerCase().includes(q)),
    );
  }, [targets, search, house, gender, family]);

  if (loading) return <div className="bl-sub py-8 text-center" data-testid="explore-loading">جارٍ تحميل المكتبة…</div>;
  if (err) return <div className="bl-sub py-8 text-center" style={{ color: "var(--bl-danger)" }} data-testid="explore-error">{err}</div>;

  return (
    <div data-testid="blend-explore-page">
      <div className="grid gap-2 mb-4" style={{ gridTemplateColumns: "2fr 1fr 1fr 1fr" }}>
        <input className="bl-input" placeholder="ابحث بالاسم / البيت / النوتة…" value={search}
          onChange={(e) => setSearch(e.target.value)} data-testid="explore-search-input" />
        <select className="bl-select" value={house} onChange={(e) => setHouse(e.target.value)} data-testid="explore-house-filter">
          <option value="">كل البيوت</option>
          {houses.map((h) => <option key={h} value={h}>{h}</option>)}
        </select>
        <select className="bl-select" value={gender} onChange={(e) => setGender(e.target.value)} data-testid="explore-gender-filter">
          <option value="">الكل (M/W/U)</option>
          <option value="M">رجالي</option>
          <option value="W">نسائي</option>
          <option value="U">للجنسين</option>
        </select>
        <select className="bl-select" value={family} onChange={(e) => setFamily(e.target.value)} data-testid="explore-family-filter">
          <option value="">كل العائلات</option>
          {families.map((f) => <option key={f.key} value={f.key}>{f.ar}</option>)}
        </select>
      </div>

      <div className="bl-sub mb-3" data-testid="explore-count">{filtered.length} هدف</div>
      <div className="bl-grid">
        {filtered.map((t) => (
          <div key={t.id} className="bl-card hoverable" data-testid={`target-card-${t.id}`}
            onClick={() => nav(`/pureh/blend/target/${t.id}`)}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-extrabold bl-en" style={{ fontSize: 15 }}>{t.name}</div>
                <div className="bl-sub bl-en">{t.house} · {t.year}</div>
              </div>
              <span className="bl-pill">{GENDER_AR[t.gender]}</span>
            </div>
            <div className="mt-2"><span className="bl-pill">{t.family_ar}</span></div>
            <div className="bl-sub bl-en mt-2" style={{ fontSize: 11 }}>DNA: {t.dna}</div>
          </div>
        ))}
      </div>
      {!filtered.length && <div className="bl-sub py-8 text-center" data-testid="explore-empty">مفيش نتائج مطابقة</div>}
    </div>
  );
}
